import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"

const MEASUREMENT_ID = "G-4ZE5GD9L4W"

type Gtag = (command: string, eventName: string, params?: Record<string, string>) => void

declare global {
  interface Window {
    gtag?: Gtag
  }
}

export function isInternalEmail(email?: string) {
  if (!email) return false
  const value = email.toLowerCase()
  return value.includes("cd2") || value.includes("clevergoose") || value.includes("bravegoose")
}

export function shouldTrackAnalytics() {
  if (typeof window === "undefined") return false
  if (process.env.NODE_ENV === "development") return false
  const host = window.location.hostname
  if (host === "localhost" || host === "127.0.0.1") return false
  if (window.top !== window.self) return false
  if (window.location.pathname === "/is-logged-in") return false
  return typeof window.gtag === "function"
}

export function trackPageView(pathname: string) {
  if (!shouldTrackAnalytics()) return
  const pagePath = pathname || "/"
  window.gtag!("event", "page_view", {
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
    page_title: pagePath,
    send_to: MEASUREMENT_ID,
  })
}

export function trackEvent(name: string, params: Record<string, string> = {}) {
  if (!shouldTrackAnalytics()) return
  window.gtag!("event", name, {
    ...params,
    send_to: MEASUREMENT_ID,
    transport_type: "beacon",
  })
}

export function useAnalyticsPageViews(isLoaded: boolean, isAuthenticated: boolean) {
  const location = useLocation()
  const internalRef = useRef<boolean | null>(null)
  const sentInternalLandingRef = useRef(false)

  useEffect(() => {
    if (!isLoaded || !shouldTrackAnalytics()) return
    let cancelled = false

    async function send() {
      if (!isAuthenticated) {
        internalRef.current = null
        sentInternalLandingRef.current = false
        if (!cancelled) trackPageView(location.pathname)
        return
      }
      if (internalRef.current === null) {
        try {
          const me = await window.skeem.fetchMe<{ skeem_email?: string }>(["skeem_email"])
          if (cancelled) return
          internalRef.current = isInternalEmail(me?.skeem_email)
        } catch {
          if (cancelled) return
          internalRef.current = false
        }
      }
      if (cancelled) return
      if (internalRef.current) {
        if (sentInternalLandingRef.current) return
        sentInternalLandingRef.current = true
      }
      trackPageView(location.pathname)
    }

    send()
    return () => {
      cancelled = true
    }
  }, [isLoaded, isAuthenticated, location.pathname])
}
